/* LALO ring size picker (2026-10-15, plans/2026-10-15-ring-size-picker.md).
   The size is a line item property (a hidden input on the product form), never a variant: every ring keeps
   6 variants. This element opens the in-page list, writes the input, blocks "add to cart" until a size (or
   "not sure") is chosen, and keeps PayPal locked until then. Loaded on every page, because the quick-add
   modal renders the product section on collection and product pages too. */
(() => {
  if (customElements.get('lalo-ring-size')) return;

  const KEY = 'lalo-ring-size:';
  const read = (k) => { try { return window.sessionStorage.getItem(k); } catch (e) { return null; } };
  const write = (k, v) => { try { window.sessionStorage.setItem(k, v); } catch (e) { /* private mode */ } };

  class LaloRingSize extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.row = this.querySelector('.lalo-ring-size__row');
      this.list = this.querySelector('.lalo-ring-size__list');
      this.value = this.querySelector('.lalo-ring-size__value');
      this.input = this.querySelector('.lalo-ring-size__input');
      this.error = this.querySelector('.lalo-ring-size__error');
      this.how = this.querySelector('.lalo-ring-size__how');
      this.howToggle = this.querySelector('.lalo-ring-size__how-toggle');
      this.options = Array.from(this.list.querySelectorAll('[role="option"]'));

      // The quick-add modal rewrites ids, so the form is found from where the picker sits, and only then
      // from the id it was rendered with.
      const scope = this.closest('[id^="MainProduct-"], .product, quick-add-modal') || document;
      this.form = scope.querySelector('form[data-type="add-to-cart-form"]') || document.getElementById(this.dataset.formId);
      if (this.form) {
        const formId = this.form.getAttribute('id'); // not form.id: the form has an input named "id"
        if (formId) this.input.setAttribute('form', formId);
        this.form.laloRingSize = this;
      }

      this.row.addEventListener('click', () => (this.list.hidden ? this.open(false) : this.close(false)));
      this.row.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); this.open(true); }
      });
      this.list.addEventListener('click', (e) => {
        const o = e.target.closest('[role="option"]');
        if (o) this.choose(o, true);
      });
      this.list.addEventListener('keydown', (e) => this.onKey(e));
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !this.list.hidden) { e.stopPropagation(); this.close(true); } });
      this.outside = (e) => { if (!this.list.hidden && !this.contains(e.target)) this.close(false); };
      document.addEventListener('click', this.outside);
      if (this.howToggle) {
        this.howToggle.addEventListener('click', () => {
          this.how.hidden = !this.how.hidden;
          this.howToggle.setAttribute('aria-expanded', String(!this.how.hidden));
        });
      }

      // Capture on the document runs before the theme's own submit handler (product-form.js).
      this.guard = (e) => {
        if (e.target !== this.form || this.input.value) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        this.require();
      };
      document.addEventListener('submit', this.guard, true);

      const saved = read(KEY + this.dataset.product);
      const again = saved && this.options.find((o) => o.dataset.value === saved);
      if (again) this.choose(again, false);
      this.lockPayment();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.outside);
      document.removeEventListener('submit', this.guard, true);
      if (this.observer) this.observer.disconnect();
      this.ready = false;
    }

    open(focus) {
      this.list.hidden = false;
      this.row.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');
      const current = this.options.find((o) => o.getAttribute('aria-selected') === 'true');
      const target = current || this.options[0];
      if (current) current.scrollIntoView({ block: 'nearest' });
      if (focus) target.focus();
    }

    close(focusRow) {
      this.list.hidden = true;
      this.row.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-open');
      if (focusRow) this.row.focus();
    }

    onKey(e) {
      const i = this.options.indexOf(document.activeElement);
      let next = null;
      if (e.key === 'ArrowDown') next = this.options[Math.min(i + 1, this.options.length - 1)];
      else if (e.key === 'ArrowUp') next = this.options[Math.max(i - 1, 0)];
      else if (e.key === 'Home') next = this.options[0];
      else if (e.key === 'End') next = this.options[this.options.length - 1];
      else if ((e.key === 'Enter' || e.key === ' ') && i >= 0) { e.preventDefault(); this.choose(this.options[i], true); return; }
      else if (e.key === 'Tab') { this.close(false); return; }
      if (next) { e.preventDefault(); next.focus(); }
    }

    choose(option, fromUser) {
      this.options.forEach((o) => o.setAttribute('aria-selected', String(o === option)));
      this.input.value = option.dataset.value;
      this.value.textContent = option.dataset.display || option.dataset.value;
      this.classList.add('has-value');
      this.classList.remove('has-error');
      this.error.hidden = true;
      write(KEY + this.dataset.product, option.dataset.value);
      this.lockPayment();
      if (fromUser) this.close(true);
      this.dispatchEvent(new CustomEvent('lalo:ring-size', { bubbles: true, detail: { value: this.input.value } }));
    }

    /* Called by the card button and by the guard: false (and the list opened) while no size is chosen. */
    ensure() {
      if (this.input.value) return true;
      this.require();
      return false;
    }

    require() {
      this.classList.add('has-error');
      this.error.hidden = false;
      this.scrollIntoView({ block: 'center', behavior: 'smooth' });
      this.open(true);
    }

    /* PayPal's button lives in an iframe: an overlay over it takes the click until a size is chosen. */
    lockPayment() {
      if (!this.form) return;
      const holder = this.form.querySelector('.shopify-payment-button');
      if (!holder) {
        if (!this.observer) {
          this.observer = new MutationObserver(() => {
            if (this.form.querySelector('.shopify-payment-button')) { this.observer.disconnect(); this.observer = null; this.lockPayment(); }
          });
          this.observer.observe(this.form, { childList: true, subtree: true });
        }
        return;
      }
      const box = holder.parentElement;
      let shield = box.querySelector('.lalo-pay-shield');
      if (!this.input.value) {
        box.classList.add('lalo-pay-locked');
        box.dataset.locked = this.dataset.locked || '';
        if (!shield) {
          shield = document.createElement('button');
          shield.type = 'button';
          shield.className = 'lalo-pay-shield';
          shield.setAttribute('aria-label', this.dataset.locked || '');  // no visible text: it would sit on the PayPal logo
          shield.addEventListener('click', () => this.require());
          box.appendChild(shield);
        }
      } else {
        box.classList.remove('lalo-pay-locked');
        if (shield) shield.remove();
      }
    }
  }

  customElements.define('lalo-ring-size', LaloRingSize);
})();
